import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import { getPopulationRequirement } from '../../../features/controls/populationRequirement.ts';
// ── AICPA AS 2301 recommended sample sizes ──────────────────────────────────
const AICPA_GUIDE = {
  annual:       { label: 'Annual',       min: 1,   max: 1,   note: 'Walkthrough only — 1 sample required.' },
  'semi-annual':{ label: 'Semi-Annual',  min: 1,   max: 2,   note: 'Typically 1 sample per half-year period.' },
  quarterly:    { label: 'Quarterly',    min: 2,   max: 5,   note: '2 samples per quarter is PCAOB-acceptable for low-risk controls.' },
  monthly:      { label: 'Monthly',      min: 3,   max: 5,   note: '3–5 samples; 5 is the AICPA safe-harbor for monthly controls.' },
  'semi-monthly':{ label:'Semi-Monthly', min: 5,   max: 8,   note: '5–8 samples depending on risk assessment.' },
  'bi-weekly':  { label: 'Bi-Weekly',    min: 8,   max: 15,  note: '8–15 samples; use upper bound for significant accounts.' },
  weekly:       { label: 'Weekly',       min: 15,  max: 25,  note: '15–25 samples; 25 is the AICPA safe-harbor for weekly.' },
  daily:        { label: 'Daily',        min: 25,  max: 60,  note: 'Minimum 25 random days; 40–60 for high-risk daily controls.' },
  'event-based':{ label: 'Event-Based',  min: 25,  max: 60,  note: 'Based on population size; minimum 25 per PCAOB AS 2301.' },
};

const FREQUENCY_MAP = {
  annual:       { label: 'Annual',      count: 1   },
  'semi-annual':{ label: 'Semi-Annual', count: 2   },
  quarterly:    { label: 'Quarterly',   count: 4   },
  monthly:      { label: 'Monthly',     count: 12  },
  'semi-monthly':{ label:'Semi-Monthly',count: 24  },
  weekly:       { label: 'Weekly',      count: 52  },
  'bi-weekly':  { label: 'Bi-Weekly',   count: 26  },
  daily:        { label: 'Daily',       count: 365 },
  'event based':{ label: 'Event-Based', count: null},
  'event-based':{ label: 'Event-Based', count: null},
};

function normalizeFreq(raw) {
  const f = (raw || 'annual').toLowerCase().replace(/_/g, '-');
  if (f === 'half-yearly') return 'semi-annual';
  if (f === 'event based') return 'event-based';
  return f;
}

function generatePeriods(frequency, year) {
  const freq = frequency?.toLowerCase();
  const y = parseInt(year, 10);
  if (freq === 'annual')
    return [{ label: `FY ${y}`, start: `${y}-01-01`, end: `${y}-12-31` }];
  if (freq === 'semi-annual')
    return [
      { label: `H1 ${y}`, start: `${y}-01-01`, end: `${y}-06-30` },
      { label: `H2 ${y}`, start: `${y}-07-01`, end: `${y}-12-31` },
    ];
  if (freq === 'quarterly')
    return [
      { label: `Q1 ${y}`, start: `${y}-01-01`, end: `${y}-03-31` },
      { label: `Q2 ${y}`, start: `${y}-04-01`, end: `${y}-06-30` },
      { label: `Q3 ${y}`, start: `${y}-07-01`, end: `${y}-09-30` },
      { label: `Q4 ${y}`, start: `${y}-10-01`, end: `${y}-12-31` },
    ];
  if (freq === 'monthly') {
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const ends   = [31,28,31,30,31,30,31,31,30,31,30,31];
    if ((y % 4 === 0 && y % 100 !== 0) || y % 400 === 0) ends[1] = 29;
    return months.map((m, i) => ({
      label: `${m} ${y}`,
      start: `${y}-${String(i+1).padStart(2,'0')}-01`,
      end:   `${y}-${String(i+1).padStart(2,'0')}-${String(ends[i]).padStart(2,'0')}`,
    }));
  }
  if (freq === 'bi-weekly') {
    const periods = []; let d = new Date(`${y}-01-01`); let num = 1;
    while (d.getFullYear() === y && num <= 26) {
      const start = d.toISOString().split('T')[0];
      d.setDate(d.getDate() + 13);
      const end = d.toISOString().split('T')[0];
      periods.push({ label: `BW${num} ${y}`, start, end });
      d.setDate(d.getDate() + 1); num++;
    }
    return periods;
  }
  if (freq === 'weekly') {
    const periods = []; let d = new Date(`${y}-01-01`); let num = 1;
    while (d.getFullYear() === y && num <= 52) {
      const start = d.toISOString().split('T')[0];
      d.setDate(d.getDate() + 6);
      const end = d.toISOString().split('T')[0];
      periods.push({ label: `Wk${num} ${y}`, start, end });
      d.setDate(d.getDate() + 1); num++;
    }
    return periods;
  }
  return [];
}

function pickRandomDays(start, end, count) {
  const startMs = new Date(start).getTime();
  const endMs   = new Date(end).getTime();
  const dayMs   = 86400000;
  const totalDays = Math.floor((endMs - startMs) / dayMs) + 1;
  if (count >= totalDays)
    return Array.from({ length: totalDays }, (_, i) => {
      const d = new Date(startMs + i * dayMs);
      return d.toISOString().split('T')[0];
    });
  const indices = new Set();
  while (indices.size < count) indices.add(Math.floor(Math.random() * totalDays));
  return Array.from(indices).sort((a, b) => a - b).map(i => {
    const d = new Date(startMs + i * dayMs);
    return d.toISOString().split('T')[0];
  });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function SectionHeader({ label, step }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <div className="h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center text-[10px] font-bold shrink-0">
        {step}
      </div>
      <h3 className="text-sm font-semibold text-gray-800">{label}</h3>
    </div>
  );
}

function SectionDivider() {
  return <div className="border-t border-gray-100 my-3" />;
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * TestSetupStep — Step 1 of the new 2-step wizard.
 *
 * Captures all test metadata on a single screen:
 *   A. Test Objective: TOD | TOE
 *   B. Testing Method (TOE only): Holistic | Attribute-by-attribute
 *   C. Sampling Method: Statistical | Judgmental
 *   D. Testing Period: year + periods (absorbs PeriodSelector)
 *   E. Roll-forward checkbox
 *   F. Population: required (default) | not required — samples are then the evidence files
 *      the auditor uploads after the run is created. Defaults from the control's test plan;
 *      the auditor can override it for this run. No reason is asked here — that is captured
 *      on the results page once testing is done.
 *
 * onComplete({ testType, testingMethod, samplingMethod, periodData, attributes, populationStatus })
 *   testType:      'tod' | 'toe'
 *   testingMethod: 'holistic' | 'attribute'  (only meaningful for TOE)
 *   samplingMethod:'statistical' | 'judgmental'
 *   periodData:    { frequency, year, periods, selectedDates?, isEventBased?, isRollforward,
 *                   testing_period_start, testing_period_end }
 *   attributes:    array from control.test_attributes (read-only context for auditor)
 *   populationStatus: 'provided' | 'not_required'
 */
export default function TestSetupStep({
  control,
  onComplete,
  initialTestType      = 'toe',
  initialTestingMethod = 'holistic',
  initialSamplingMethod = 'statistical',
  initialPeriodData    = null,
  initialPopulationStatus = null,
}) {
  const freq     = normalizeFreq(control?.frequency);
  // Population: a resumed run keeps its own status; a new run starts from the test plan.
  const planPopulation = getPopulationRequirement(control);
  const [populationNotRequired, setPopulationNotRequired] = useState(
    initialPopulationStatus ? initialPopulationStatus === 'not_required' : !planPopulation.required
  );
  const freqInfo = FREQUENCY_MAP[freq] || FREQUENCY_MAP['annual'];
  const isDaily  = freq === 'daily';
  const isEvent  = freq === 'event-based';
  const aicpa    = AICPA_GUIDE[freq] || AICPA_GUIDE['event-based'];

  // Test Objective
  const [testType,       setTestType]       = useState(initialTestType);
  // Always attribute-based in new flow
  const testingMethod = 'attribute';
  // Testing Period
  const [year,           setYear]           = useState(() =>
    initialPeriodData?.year || new Date().getFullYear().toString()
  );
  const [selectedPeriods, setSelectedPeriods] = useState(() => initialPeriodData?.periods || []);
  const [eventStart,      setEventStart]      = useState(() => initialPeriodData?.testing_period_start || '');
  const [eventEnd,        setEventEnd]        = useState(() => initialPeriodData?.testing_period_end || '');
  // Daily random
  const [maxDays,          setMaxDays]         = useState(25);
  const [randomDates,      setRandomDates]     = useState(() => initialPeriodData?.selectedDates || []);
  const [datesGenerated,   setDatesGenerated]  = useState(() => (initialPeriodData?.selectedDates?.length || 0) > 0);

  const allPeriods = (isDaily || isEvent) ? [] : generatePeriods(freq, year);
  // One attribute per step — matches the "N attributes" count shown in the Test Design tab.
  // buildAttributeList drills into step.test_attributes[] which can produce more entries
  // than the steps count when a step has multiple nested test_attributes.
  const attrs = (control?.test_steps || []).map((step, i) => ({
    ref:   String.fromCharCode(65 + i),
    label: step.summary || step._text || step.description || `Attribute ${String.fromCharCode(65 + i)}`,
    id:    step.id || step.key || i,
  }));

  useEffect(() => {
    setSelectedPeriods([]);
    setDatesGenerated(false);
    setRandomDates([]);
  }, [year, freq]);

  const togglePeriod = (p) =>
    setSelectedPeriods(prev =>
      prev.find(x => x.label === p.label) ? prev.filter(x => x.label !== p.label) : [...prev, p]
    );

  const handleGenerateDates = () => {
    const count = Math.max(25, parseInt(maxDays, 10) || 25);
    const start = control?.testing_period_start
      ? String(control.testing_period_start).split('T')[0] : `${year}-01-01`;
    const end   = control?.testing_period_end
      ? String(control.testing_period_end).split('T')[0]   : `${year}-12-31`;
    const dates = pickRandomDays(start, end, count);
    setRandomDates(dates);
    setDatesGenerated(true);
  };

  const periodReady = isEvent
    ? !!(eventStart && eventEnd && eventEnd >= eventStart)
    : isDaily
    ? datesGenerated && randomDates.length > 0
    : selectedPeriods.length > 0;

  const handleContinue = () => {
    if (!periodReady) return;

    let periodData;
    if (isEvent) {
      periodData = {
        frequency: freq, year,
        periods: [{ label: `${eventStart} — ${eventEnd}`, start: eventStart, end: eventEnd }],
        isEventBased: true, isRollforward: false,
        testing_period_start: eventStart, testing_period_end: eventEnd,
      };
    } else if (isDaily) {
      periodData = {
        frequency: freq, year,
        periods: randomDates.map(d => ({ label: d, start: d, end: d })),
        selectedDates: randomDates, isRollforward: false,
        testing_period_start: randomDates[0], testing_period_end: randomDates[randomDates.length - 1],
      };
    } else {
      const sorted = [...selectedPeriods].sort((a, b) => (a.start > b.start ? 1 : -1));
      periodData = {
        frequency: freq, year, periods: sorted, isRollforward: false,
        testing_period_start: sorted[0]?.start,
        testing_period_end:   sorted[sorted.length - 1]?.end,
      };
    }

    onComplete({
      testType,
      testingMethod: testType === 'tod' ? 'holistic' : testingMethod,
      samplingMethod: testType === 'tod' ? 'judgmental' : initialSamplingMethod,
      periodData,
      attributes: attrs,
      populationStatus: populationNotRequired ? 'not_required' : 'provided',
    });
  };

  return (
    <div className="px-5 py-4 space-y-0 overflow-y-auto">

      {/* ── Test Objective — card selectors ── */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {[
          {
            id: 'tod',
            abbr: 'TOD',
            title: 'Test of Design',
            activeBorder: 'border-violet-500',
            activeBg: 'bg-violet-50',
            badge: 'bg-violet-600',
            activeTitle: 'text-violet-800',
          },
          {
            id: 'toe',
            abbr: 'TOE',
            title: 'Operating Effectiveness',
            activeBorder: 'border-blue-500',
            activeBg: 'bg-blue-50',
            badge: 'bg-blue-600',
            activeTitle: 'text-blue-800',
          },
        ].map(({ id, abbr, title, activeBorder, activeBg, badge, activeTitle }) => {
          const active = testType === id;
          return (
            <button
              key={id}
              type="button"
              onClick={() => setTestType(id)}
              className={`rounded-xl border-2 p-3 text-left transition-all duration-150 ${
                active
                  ? `${activeBorder} ${activeBg} shadow-sm`
                  : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-3">
                <span className={`shrink-0 px-2 py-0.5 rounded-md text-[11px] font-bold text-white ${badge}`}>
                  {abbr}
                </span>
                <div className={`text-sm font-semibold leading-tight self-center ${active ? activeTitle : 'text-gray-700'}`}>
                  {title}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {/* ── Testing Period ── */}
      <SectionDivider />
      <div className="flex items-center gap-2 mb-3">
        <span className="text-sm font-semibold text-gray-800">Testing Period</span>
      </div>

      {!isEvent && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-1">Testing Year</label>
          <select value={year} onChange={e => setYear(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 w-36">
            {[2023,2024,2025,2026,2027].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      )}

      {!isDaily && !isEvent && allPeriods.length > 0 && (
        <div className="mb-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">Select Periods</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {allPeriods.map(p => {
              const active = !!selectedPeriods.find(x => x.label === p.label);
              return (
                <button key={p.label} onClick={() => togglePeriod(p)}
                  className={`px-3 py-2 rounded-lg text-xs border transition-colors ${
                    active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'
                  }`}>
                  {p.label}
                </button>
              );
            })}
          </div>
          <button onClick={() => setSelectedPeriods(allPeriods)} className="mt-2 text-[11px] text-blue-600 hover:underline">
            Select all
          </button>
        </div>
      )}

      {isDaily && (
        <div className="space-y-3 mb-3">
          <div className="flex items-center gap-3">
            <input type="number" min={25} value={maxDays} onChange={e => setMaxDays(e.target.value)}
              className="w-24 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            <button onClick={handleGenerateDates}
              className="px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700">
              Pick Random Days
            </button>
            <span className="text-xs text-gray-500">(min 25 per AICPA)</span>
          </div>
          {datesGenerated && (
            <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-lg p-2 grid grid-cols-3 md:grid-cols-6 gap-1">
              {randomDates.map(d => <span key={d} className="text-[10px] bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded">{d}</span>)}
            </div>
          )}
        </div>
      )}

      {isEvent && (
        <div className="space-y-2 mb-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period start <span className="text-red-500">*</span></label>
              <input type="date" value={eventStart} onChange={e => setEventStart(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period end <span className="text-red-500">*</span></label>
              <input type="date" value={eventEnd} min={eventStart || undefined} onChange={e => setEventEnd(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
            </div>
          </div>
          {eventStart && eventEnd && eventEnd < eventStart && (
            <p className="text-xs text-red-500">End date must be on or after start date.</p>
          )}
        </div>
      )}

      {/* ── Population ── */}
      <SectionDivider />
      <label className="flex items-start gap-2 mb-4 cursor-pointer">
        <input
          type="checkbox"
          checked={populationNotRequired}
          onChange={e => setPopulationNotRequired(e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
        <span>
          <span className="block text-sm font-semibold text-gray-800">No population file needed for this test</span>
          <span className="block text-xs text-gray-500">
            Samples will be the evidence files you upload; you can group several files under one sample.
            {planPopulation.assessed && (
              <> Test plan says population is <strong>{planPopulation.required ? 'required' : 'not required'}</strong>
              {planPopulation.source === 'ai' ? ' (AI suggested)' : planPopulation.source === 'auditor' ? ' (set by auditor)' : ''}.</>
            )}
          </span>
        </span>
      </label>

      {/* ── Continue ── */}
      <div className="pt-1">
        <button
          onClick={handleContinue}
          disabled={!periodReady}
          className="px-6 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {populationNotRequired ? 'Create Test Run' : 'Continue to Population →'}
        </button>
      </div>
    </div>
  );
}
